<template>
    <div class="f fv pr ath-form-options" :class="[disabled ? 'el--disabled' : '']">
        <div class="f fh fa-c dw-10 sm-b-xs" v-if="lbl">
            <slot name="labelLeft"></slot>
            <span class="fg ath-form-options-label">{{ uiTitle }}<slot name="labelAfter"></slot></span>
            <slot name="labelRight"></slot>
        </div>
        <div class="f fw dw-10" style="margin-top: -1rem">
            <label class="fi fh fa-c sm-t-s sm-r-s sp-l-s sp-r-s br-m oh o-ol ath-form-options-el"
                v-for="opt in options"
                :key="opt.val"
                :for="uid + '-' + opt.val"
                :title="disabled || (!canDeselect && (data[opt.val] && data[opt.val].active)) ? uiDisabledMessage : ''"
                :class="[
                    (data[opt.val] && data[opt.val].active) ? 'el--active' : 'el--inactive',
                    disabled || (!canDeselect && (data[opt.val] && data[opt.val].active)) ? 'c-na el--disabled' : 'c-p',
                    opt.cname ? opt.cname : '',
                ]">
                <input class="u-hide"
                    :id="uid + '-' + opt.val"
                    :name="uid"
                    :type="multi ? 'checkbox' : 'radio'"
                    v-model="data[opt.val].active"
                    v-if="data[opt.val]"
                    :disabled="disabled || (!canDeselect && (data[opt.val] && data[opt.val].active))"
                    :qa="`form-option-${opt.lbl}`"
                    @change="onChange(opt.val)" />
                <slot name="optionLeft" :data="opt"></slot>
                <VisualIcon class="sm-r-xs" uiSize="m" :name="opt.icon" v-if="opt.icon" />
                <span class="fns u-us-n o-ol ath-form-options-el-label" v-if="opt.lbl">{{ opt.lbl | athT }}</span>
                <slot name="optionRight" :data="opt"></slot>
            </label>
        </div>
    </div>
</template>

<script>
import { guuid, isNeArray, isNumber, isObject, isString, join, mapPrimitive } from '@aretheyhappy/utils';
import VisualIcon from '../visuals/Icon.vue';

export default {
    name: 'mead-form-options',
    components: {VisualIcon},
    props: {
        /**
         * Label i18n key rendered above the option list. Only shown when truthy.
         */
        lbl         : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it.
         */
        lblEntity   : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc : {type: Boolean, default: () => true},
        /**
         * Currently selected value(s) (v-model): an array of values when `multi` is true, otherwise
         * a single primitive value (or `false` when nothing is selected).
         */
        value       : {type: [Boolean, String, Number, Array], default: () => false},
        /**
         * List of option objects (`{val, lbl, icon, cname}`) rendered as toggleable pills.
         */
        options     : {type: Array, default: () => []},
        /**
         * Whether multiple options can be active at once (checkbox-like) instead of a single one
         * (radio-like).
         */
        multi       : {type: Boolean, default: () => true},
        /**
         * Disables every option.
         */
        disabled    : {type: Boolean, default: () => false},
        /**
         * Appends an "(optional)" suffix to the label.
         */
        uiOptional  : {type: Boolean, default: () => false},
        /**
         * Minimum number of options that must remain active; active options are prevented from being
         * deselected once this threshold is reached.
         */
        min         : {type: Number, default: 0},
        /**
         * i18n key for the tooltip shown on an option that can't currently be toggled (disabled, or
         * blocked by the `min` threshold).
         */
        disabledMessage: {type: String, default: ''}
    },
    data () {
        return {
            uid: guuid(),
            data: {}
        };
    },
    methods: {
        buildDataFromOptions: function() {
            //  Get selected value map
            const v_map = this.multi
                ? mapPrimitive(this.value)
                : this.value;

            //  Create data map with options, preset them to whether or not they were selected or not
            const data = {};
            if (isNeArray(this.options)) {
                for (const el of this.options) {
                    if (
                        !isObject(el) ||
                        (!isString(el.val) && !isNumber(el.val))
                    ) continue;

                    const key = `${el.val}`.trim();
                    data[key] = {
                        active  : this.multi ? !!v_map.hasOwnProperty(el.val) : v_map === el.val,
                        val     : el.val,
                        opt     : el,
                    };
                }
            }
            this.data = data;
        },
        //  Send an update to the parent
        notify: function () {
            //  Flatten the selected dataset into an array containing the selected values
            const selected = [];
            for (const k of Object.keys(this.data)) {
                if (this.data[k].active !== true) continue;
                selected.push(this.data[k].val);
            }

            //  If multi send out entire dataset, otherwise only the first one or false
            if (this.multi) {
                this.$emit('input', selected);
            } else {
                this.$emit('input', isNeArray(selected) ? selected[0] : false);
            }
        },
        onChange: function (val) {
            if (this.multi) {
                this.notify();
                return;
            }

            //  Reset all existing ones
            const s = `${val}`.trim();
            for (const k of Object.keys(this.data)) {
                if (k === s || !this.data[k].active) continue;
                this.data[k].active = false;
            }

            //  Set new active one
            this.data[s].active = true;

            this.notify();
        },
    },
    watch: {
        options: function () {
            this.buildDataFromOptions();
        },
        value: function () {
            this.buildDataFromOptions();
        }
    },
    computed: {
        canDeselect: function () {
            return this.activeCount > this.min;
        },
        activeCount: function () {
            let count = 0;
            for (const key in this.data) {
                if (this.data[key].active) count++;
            }
            return count;
        },
        uiDisabledMessage: function () {
            return MeadCommon.I18N.go(this.disabledMessage);
        },
        uiTitle: function () {
            return join([
                this.lblEntity
                    ? MeadCommon.I18N[this.lblEntityLc ? 'actionLc' : 'action'](this.lbl, this.lblEntity)
                    : MeadCommon.I18N.go(this.lbl),
                this.uiOptional
                    ? `(${MeadCommon.I18N.go('mead.labels.optional')})`
                    : false,
            ], {delim: ' '});
        },
    },
    created: function () {
        this.buildDataFromOptions();
    }
};
</script>

<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

.ath-form-options-label {
    font-family: $ff-b;
    font-weight: 400;
    line-height: $lh-xs;
    font-size: $fs-m;
    color: var(--c-txt);
}

.ath-form-options-el {
    height: 3rem;
    line-height: 3rem;
    width: auto;
    background-color: var(--c-brand-light-bg);
    color: var(--c-brand-light-fg);

    &.el--active {
        background-color: var(--c-brand-active-bg);
        color: var(--c-brand-active-fg);
    }

    &.el--disabled {
        cursor: not-allowed;
        opacity: .65;
    }
}

.ath-form-options {
    &:not(.el--disabled) {
        .ath-form-options-el:hover {
            background-color: var(--c-brand-active-bg);
            color: var(--c-brand-active-fg);
        }
    }

    &.el--disabled .ath-form-options-el {
        cursor: not-allowed;
        opacity: .65;
    }
}

.ath-form-options-el-label {
    font-weight: 400;
    font-size: $fs-s;
    font-family: $ff-b;
    letter-spacing: $ls-s;
}
</style>
